/**
 * Validates data channel messages according to T.140 protocol standards.
 *
 * T.140 is the ITU-T recommendation for text conversation protocol used in
 * real-time text communication over data channels.
 *
 * Validation rules:
 * - Rejects Unicode replacement character (U+FFFD) which indicates corruption
 * - Allows: Backspace (0x08), Tab (0x09), Line Feed (0x0A), Carriage Return (0x0D)
 * - Allows: All printable characters (0x20-0x7E and above 0x7F)
 * - Rejects: Other control characters (0x00-0x07, 0x0B, 0x0C, 0x0E-0x1F, 0x7F)
 *
 * @param data - The message data to validate (string, ArrayBuffer, or Uint8Array)
 * @returns true if valid, false if invalid
 */
export const validateDataChannelMessage = (
  data: string | ArrayBuffer | Uint8Array
): boolean => {
  // Only validate string messages - binary data is passed through
  if (typeof data !== 'string') {
    return true;
  }

  // Filter Unicode replacement character (U+FFFD)
  // This character indicates corruption or noise in T.140 transmission
  if (data.includes('\ufffd')) {
    return false;
  }

  // T.140 compliant character validation
  // eslint-disable-next-line no-control-regex
  const hasInvalidControlChars = /[\x00-\x07\x0B\x0C\x0E-\x1F\x7F]/.test(data);

  if (hasInvalidControlChars) {
    return false;
  }

  return true;
};
